import { useServices } from '@/app/providers/ServiceContext';
import { TenantPicker } from '@/presentation/components/molecules/TenantPicker';
import { useLoginRateLimit } from '@/shared/hooks/useLoginRateLimit';
import { TenantSelectionRequiredError } from '@yawi/domain';
import { ArrowRight, Eye, EyeOff, Info, Lock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import type { TenantSelection } from '@yawi/types';

export const Login = () => {
  const { login, selectTenantForMagicLink, systemSettings, currentUser } = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // El correo puede resolver a más de una cuenta (ver TenantSelectionRequiredError)
  // — se muestra un selector en vez de navegar directo.
  const [pendingTenants, setPendingTenants] = useState<TenantSelection[] | null>(null);

  const { isBackoff, isLocked, countdown, canSubmit, recordFailure, recordSuccess } = useLoginRateLimit();

  const [branding, setBranding] = useState({ appName: 'Hommi', primaryColor: '#8B5CF6', logoUrl: '' });

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    if (systemSettings?.branding) {
      setBranding({
        appName: systemSettings.branding.appName || 'Hommi',
        primaryColor: systemSettings.branding.primaryColor || '#8B5CF6',
        logoUrl: systemSettings.branding.logoUrl || ''
      });
    }
  }, [systemSettings]);

  const goToDestination = () => {
    const pendingRoute = sessionStorage.getItem('pendingNotificationRoute');
    const fromState = location.state?.from;
    if (pendingRoute) {
      sessionStorage.removeItem('pendingNotificationRoute');
      navigate(pendingRoute, { replace: true });
    } else if (fromState && fromState !== '/login') {
      navigate(fromState, { replace: true });
    } else {
      const defaultPath = currentUser?.tenantRole === 'FAMILIAR' ? '/accesos' : '/';
      navigate(defaultPath, { replace: true });
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      await login(email, pin);
      recordSuccess();
      goToDestination();
    } catch (err: any) {
      if (err instanceof TenantSelectionRequiredError) {
        // No es un fallo de credenciales — el correo es válido, solo hay que
        // elegir cuál de las cuentas coincidentes usar.
        setPendingTenants(err.tenants);
        return;
      }
      recordFailure();
      setError('Error al iniciar sesión, compruebe sus credenciales.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTenant = async (tenant: TenantSelection) => {
    setLoading(true);
    setError('');
    try {
      await selectTenantForMagicLink(tenant.userId, tenant.tenantId);
      recordSuccess();
      goToDestination();
    } catch (err: any) {
      setError('Error al seleccionar la comunidad, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Mesh gradient animado */}
      <div className="absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-purple-300/50 rounded-full blur-[100px] float-orb" />
        <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-indigo-300/40 rounded-full blur-[80px] liquid-blob" />
        <div className="absolute -bottom-32 left-1/4 w-[600px] h-[600px] bg-violet-300/40 rounded-full blur-[120px] glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-pink-200/30 rounded-full blur-[80px] float-orb" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-[100px] liquid-blob" style={{ animationDelay: '-5s' }} />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* === FORMULARIO CENTRADO === */}
      <div className="w-full flex flex-col relative z-10">
        <div className="flex-1 flex items-center justify-center px-6 sm:px-8 lg:px-12 py-8">
          {/* LIQUID GLASS CARD */}
          <div
            className="w-full max-w-md rounded-3xl p-6 lg:p-8 relative"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.08),
                0 2px 8px rgba(0, 0, 0, 0.04),
                inset 0 0 0 1px rgba(255, 255, 255, 0.5),
                inset 0 2px 0 rgba(255, 255, 255, 0.8)
              `,
            }}
          >
            {/* Top highlight (efecto de luz superior) */}
            <div
              className="absolute top-0 left-4 right-4 h-[2px] rounded-full"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
              }}
            />

            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Logo" className="w-40 h-40 object-contain" />
              ) : (
                <img src="/images/logo_vertical.svg" alt="Logo" className="w-40 h-40 object-contain" />
              )}
            </div>

            {pendingTenants ? (
              <>
                {error && (
                  <div
                    className="text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2 mb-4"
                    style={{
                      background: 'rgba(254, 226, 226, 0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(254, 202, 202, 0.8)',
                    }}
                  >
                    <Info size={16} /> {error}
                  </div>
                )}
                <TenantPicker tenants={pendingTenants} loading={loading} onConfirm={handleSelectTenant} />
                <button
                  type="button"
                  onClick={() => { setPendingTenants(null); setError(''); }}
                  className="mt-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Volver
                </button>
              </>
            ) : (
            <>
            {/* Welcome text */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-800">
                Bienvenido de vuelta
              </h1>
              <p className="mt-1 text-slate-500 text-sm">
                Ingresa a tu comunidad
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Credential error (only shown when no penalty is active) */}
              {error && !isBackoff && !isLocked && (
                <div
                  className="text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                  style={{
                    background: 'rgba(254, 226, 226, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(254, 202, 202, 0.8)',
                  }}
                >
                  <Info size={16} /> {error}
                </div>
              )}

              {/* Soft backoff — 3 failures */}
              {isBackoff && (
                <div
                  className="text-amber-700 px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: 'rgba(254, 243, 199, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(253, 230, 138, 0.9)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <Info size={15} className="shrink-0" />
                    <span className="font-semibold">Demasiados intentos</span>
                  </div>
                  <p className="pl-[23px] text-amber-600">
                    Espera <strong className="text-amber-700">{countdown}s</strong> antes de volver a intentarlo.
                  </p>
                </div>
              )}

              {/* Hard lock — 5 failures */}
              {isLocked && (
                <div
                  className="text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2"
                  style={{
                    background: 'rgba(254, 226, 226, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(254, 202, 202, 0.95)',
                  }}
                >
                  <Lock size={16} />
                  <span>
                    Cuenta bloqueada temporalmente. Espera <strong>{countdown}s</strong> o contacta a soporte.
                  </span>
                </div>
              )}

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full rounded-xl px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)',
                  }}
                  onFocus={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                    e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                  }}
                  onBlur={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                    e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                    e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                  }}
                />
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Contraseña
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={pin}
                    onChange={e => setPin(e.target.value)}
                    required
                    className="w-full rounded-xl px-4 py-3 pr-12 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-all duration-300"
                    style={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.02)',
                    }}
                    onFocus={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                      e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                    }}
                    onBlur={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.7)';
                      e.target.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                      e.target.style.boxShadow = 'inset 0 2px 4px rgba(0, 0, 0, 0.02)';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none focus:text-purple-60"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !canSubmit}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white
                         flex items-center justify-center gap-2 transition-all duration-300
                         disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                  boxShadow: `
                    0 4px 16px rgba(139, 92, 246, 0.35),
                    inset 0 1px 0 rgba(255, 255, 255, 0.2)
                  `,
                }}
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isLocked ? (
                  <>
                    <Lock size={16} />
                    Bloqueado ({countdown}s)
                  </>
                ) : isBackoff ? (
                  <>Espera {countdown}s</>
                ) : (
                  <>
                    Acceder
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
            </>
            )}

            {/* Security badge */}
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
              <Lock size={12} />
              <span className="text-xs">Conexión segura</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 px-6 sm:px-8 lg:px-12 py-4">
          <p className="text-slate-400 text-xs text-center">
            <a href="#" className="hover:text-purple-600 transition-colors">Términos</a>
            {' · '}
            <a href="#" className="hover:text-purple-600 transition-colors">Privacidad</a>
          </p>
          <p className="text-slate-300 text-xs mt-1 text-center">
            Powered by <span className="text-purple-500 font-medium">Xelesta</span>
          </p>
        </div>
      </div>

    </div>
  );
};